// Branded confirm dialog — replaces window.confirm() everywhere.
//
// Usage:
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Klient archivieren?', body: name, confirmLabel: 'Archivieren', destructive: true })) {
//     // user pressed OK
//   }
//
// The provider mounts a single Dialog at the app root. The hook returns a
// promise that resolves to true (confirmed) or false (cancelled).
import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';

export interface ConfirmOptions {
  title: string;
  body?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type Resolver = (value: boolean) => void;

const ConfirmCtx = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setState({ opts, resolve });
      }),
    [],
  );

  const handle = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  const opts = state?.opts;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => !o && handle(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{opts?.title}</DialogTitle>
          </DialogHeader>
          {opts?.body !== undefined && (
            <div className="text-sm text-ink-2 leading-relaxed">{opts.body}</div>
          )}
          {/* Batch ZO #11 — make both buttons unambiguously visible. The
              previous `ghost` cancel + thinly-styled confirm was reported
              by the tester as „only Cancel visible" — operators couldn't
              find the Bestätigen button on smaller screens. Outline
              cancel + filled confirm with destructive accent fixes that. */}
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => handle(false)}>
              {opts?.cancelLabel ?? t('common.cancel', 'Abbrechen')}
            </Button>
            <Button
              onClick={() => handle(true)}
              className={
                opts?.destructive
                  ? 'bg-[hsl(var(--negative))] text-white hover:bg-[hsl(var(--negative))]/90 font-semibold'
                  : 'bg-[hsl(var(--brand-gold-deep))] text-white hover:bg-[hsl(var(--brand-gold-deep))]/90 font-semibold'
              }
            >
              {opts?.confirmLabel ?? t('common.confirm', 'Bestätigen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
